import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { SuperAdminDashboard } from "@/components/admin/SuperAdminDashboard";
import { PlatformTenantList } from "@/components/admin/PlatformTenantList";
import { PlatformTenantCreate } from "@/components/admin/PlatformTenantCreate";
import { PlatformTenantConfig } from "@/components/admin/PlatformTenantConfig";
import { PlatformAnalytics } from "@/components/admin/PlatformAnalytics";
import { PlatformUserManagement } from "@/components/admin/PlatformUserManagement";
import { PlatformPlanList } from "@/components/admin/PlatformPlanList";
import { PlatformPlanForm } from "@/components/admin/PlatformPlanForm";
import {
  TbBuildingSkyscraper,
  TbLogout,
  TbArrowLeft,
  TbLayoutDashboard,
  TbChartBar,
  TbUsers,
  TbHistory,
  TbPackage,
} from "react-icons/tb";
import { ActivityLogs } from "@/components/admin/ActivityLogs";

const navItems = [
  { to: "/platform", label: "Overview", icon: TbLayoutDashboard, end: true },
  {
    to: "/platform/tenants",
    label: "Tenants",
    icon: TbBuildingSkyscraper,
    end: false,
  },
  { to: "/platform/plans", label: "Plans", icon: TbPackage, end: false },
  { to: "/platform/users", label: "Users", icon: TbUsers, end: false },
  {
    to: "/platform/analytics",
    label: "Analytics",
    icon: TbChartBar,
    end: false,
  },
  {
    to: "/platform/logs",
    label: "Activity Logs",
    icon: TbHistory,
    end: false,
  },
];

export const PlatformAdmin: React.FC = () => {
  const { userProfile, signOut, hasPlatformAccess } = useAuth();
  const navigate = useNavigate();

  if (!hasPlatformAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#444] mb-2">Access Denied</h1>
          <p className="text-[#828282] mb-6">
            You need a platform role to access this page.
          </p>
          <button
            onClick={() => navigate("/map")}
            className="px-6 py-3 bg-[#374795] text-white text-sm font-medium rounded-lg hover:bg-[#1c398e] transition-colors cursor-pointer"
          >
            Go to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 shadow-sm shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#374795] rounded-lg flex items-center justify-center">
              <TbBuildingSkyscraper className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-[#444]">Platform Admin</h1>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-[#828282]">
              {userProfile?.first_name} {userProfile?.last_name}
            </span>
            <button
              onClick={() => navigate("/map")}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#444] bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <TbArrowLeft className="w-4 h-4" /> Back to App
            </button>
            <button
              onClick={signOut}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
            >
              <TbLogout className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Body: Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <nav className="w-60 bg-white border-r border-gray-200 shrink-0 py-4">
          <ul className="space-y-1 px-3">
            {navItems.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[#374795]/10 text-[#374795] border-l-3 border-[#374795]"
                        : "text-[#828282] hover:bg-gray-100 hover:text-[#444]"
                    }`
                  }
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto">
            <Routes>
              <Route index element={<SuperAdminDashboard />} />
              <Route path="tenants" element={<PlatformTenantList />} />
              <Route path="tenants/new" element={<PlatformTenantCreate />} />
              <Route
                path="tenants/:tenantId"
                element={<PlatformTenantConfig />}
              />
              <Route path="plans" element={<PlatformPlanList />} />
              <Route path="plans/new" element={<PlatformPlanForm />} />
              <Route path="plans/:planId" element={<PlatformPlanForm />} />
              <Route path="users" element={<PlatformUserManagement />} />
              <Route path="analytics" element={<PlatformAnalytics />} />
              <Route path="logs" element={<ActivityLogs scope="platform" />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};
